import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  ApiResponse,
  Contestant,
  RegisterRequest,
  LoginRequest,
  LoginResponse,
  Question,
  TodayQuestionResponse,
  Answer,
  SubmitAnswerRequest,
  RamadanEvent,
} from '@/types';

// API Base URL - Update this with your Lambda API Gateway URL
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://your-api-gateway-url.amazonaws.com';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiResponse<unknown>>) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth Methods
  async register(data: RegisterRequest): Promise<ApiResponse<{ id: string; full_name: string; email: string }>> {
    const response = await this.client.post('/api/register', data);
    return response.data;
  }

  async login(data: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await this.client.post('/api/login', data);
    if (response.data.success && response.data.data) {
      localStorage.setItem('auth_token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.contestant));
    }
    return response.data;
  }

  async getCurrentUser(): Promise<ApiResponse<Contestant>> {
    const response = await this.client.get('/api/me');
    return response.data;
  }

  logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    window.location.href = '/';
  }

  // Question Methods
  async getTodayQuestion(): Promise<ApiResponse<TodayQuestionResponse>> {
    const response = await this.client.get('/api/question/today');
    return response.data;
  }

  async getQuestionById(id: string): Promise<ApiResponse<Question>> {
    const response = await this.client.get(`/api/question/${id}`);
    return response.data;
  }

  // Answer Methods
  async submitAnswer(data: SubmitAnswerRequest): Promise<ApiResponse<{ answer_id: string; submitted_at: string }>> {
    const response = await this.client.post('/api/answer', data);
    return response.data;
  }

  async getMyAnswers(): Promise<ApiResponse<Answer[]>> {
    const response = await this.client.get('/api/my-answers');
    return response.data;
  }

  // Event Methods
  async getEvents(): Promise<ApiResponse<RamadanEvent[]>> {
    const response = await this.client.get('/api/events');
    return response.data;
  }

  // Registration Status
  async getRegistrationStatus(): Promise<ApiResponse<{ open: boolean }>> {
    const response = await this.client.get('/api/registration-status');
    return response.data;
  }

}

export const api = new ApiClient();

// Helper to check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('auth_token');
};

// Helper to get stored user
export const getStoredUser = (): Contestant | null => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};
